import { INVITE_MAIL_STATUSES, type InviteMail, type InviteMessage, type StartGuide } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Copy, MailCheck, MailWarning } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Textarea } from '../../ui/form';

/** Состояние письма с инструкцией одной строкой — для списка приглашений */
export function mailLine(mail: InviteMail | null | undefined): string {
  if (!mail) return 'письмо с инструкцией не отправлялось';
  const at = mail.at ? `, ${dateTime(mail.at)}` : '';
  // Текст приглашения показывается один раз; новый текст и ссылку даёт «Отправить заново»
  const again = ' — «Отправить заново» даст новую ссылку и текст для мессенджера';
  if (mail.status === 'failed') return `письмо не ушло${mail.error ? ` (${mail.error})` : ''}${at}${again}`;
  if (mail.status === 'not_configured') return `письмо не ушло: почта портала не подключена${again}`;
  if (mail.status === 'limited')
    return `письмо не ушло: ${mail.error ?? 'предел писем'}${at} — отправьте ссылку сами или «Отправить заново» позже`;
  return `${INVITE_MAIL_STATUSES[mail.status].toLowerCase()}${mail.status === 'sent' ? at : ''}`;
}

/** Письмо не ушло и не уходит — строку состояния стоит выделить */
export function mailNeedsAttention(mail: InviteMail | null | undefined): boolean {
  return mail?.status === 'failed' || mail?.status === 'not_configured' || mail?.status === 'limited';
}

/** Копирование в буфер: удалось или нет — без буфера (старый браузер, запрет) текст выделяют вручную */
function useCopy() {
  const [state, setState] = useState<{ what: string; ok: boolean } | null>(null);
  return {
    copied: state?.ok ? state.what : null,
    failed: state && !state.ok ? state.what : null,
    copy: async (what: string, text: string): Promise<boolean> => {
      try {
        if (!navigator.clipboard) throw new Error('clipboard');
        await navigator.clipboard.writeText(text);
        setState({ what, ok: true });
        return true;
      } catch {
        setState({ what, ok: false });
        return false;
      }
    },
  };
}

/**
 * Итог приглашения (БЗ-31): ушло ли письмо с инструкцией, ссылка и текст приглашения целиком — чтобы отправить
 * его самому, если почта портала не подключена; по желанию — что именно получит человек.
 */
export function InviteResult({
  email,
  link,
  expiresAt,
  mail,
  message,
  previewId,
}: {
  email: string;
  link?: string;
  expiresAt?: string;
  mail?: InviteMail | null;
  message?: InviteMessage;
  /** Приглашение компании: можно показать, что получит человек */
  previewId?: string;
}) {
  const { copied, failed, copy } = useCopy();
  const [showText, setShowText] = useState(false);
  const sent = mail?.status === 'sent' || mail?.status === 'sending';
  const status = mail?.status;
  return (
    <div className="flex flex-col gap-4">
      <div
        className={clsx(
          'flex gap-3 rounded-md px-4 py-3 text-[14px] leading-6',
          sent ? 'bg-success-soft text-ink' : 'bg-warning-soft text-ink',
        )}
        role="status"
      >
        {sent ? (
          <MailCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
        ) : (
          <MailWarning className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
        )}
        <p>
          {status === 'sent'
            ? `Письмо со ссылкой и инструкцией «С чего начать» отправлено на ${email}: разделы по ролям, первые шаги и что делать каждый день.`
            : status === 'sending'
              ? `Письмо с инструкцией отправляется на ${email}. Состояние будет видно в списке приглашений.`
              : status === 'demo'
                ? 'В демо-компании письма не отправляются. Текст приглашения с инструкцией можно скопировать ниже.'
                : status === 'failed'
                  ? `Письмо на ${email} не ушло${mail?.error ? ` (${mail.error})` : ''}. Скопируйте приглашение с инструкцией и отправьте его сами — в мессенджере или почтой.`
                  : status === 'limited'
                    ? `Письмо на ${email} не отправлено: ${mail?.error ?? 'предел писем'}. Скопируйте приглашение с инструкцией и отправьте его сами — в мессенджере или почтой.`
                    : `Почта портала не подключена, письмо не ушло. Скопируйте приглашение с инструкцией и отправьте ${email} в мессенджере или почтой.`}
        </p>
      </div>

      {link ? (
        <div>
          <p className="mb-1.5 text-[13px] text-ink-2">
            Ссылка {expiresAt ? `действует до ${date(expiresAt)} и ` : ''}срабатывает один раз. Показывается
            только сейчас.
          </p>
          <div className="visor flex items-center gap-2 rounded-md bg-surface-2 p-3">
            <code className="min-w-0 flex-1 truncate text-[13px] text-ink">{link}</code>
            <Button
              size="sm"
              variant={copied === 'link' ? 'secondary' : 'ghost'}
              icon={copied === 'link' ? <Check className="size-4" /> : <Copy className="size-4" />}
              onClick={() => copy('link', link)}
            >
              {copied === 'link' ? 'Скопировано' : 'Ссылка'}
            </Button>
          </div>
          {failed === 'link' ? (
            <p className="mt-1.5 text-[13px] text-warning">
              Скопировать не получилось: выделите ссылку и скопируйте её вручную.
            </p>
          ) : null}
        </div>
      ) : null}

      {message ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={copied === 'text' ? 'secondary' : 'primary'}
              icon={copied === 'text' ? <Check className="size-4" /> : <Copy className="size-4" />}
              onClick={async () => {
                if (!(await copy('text', message.text))) setShowText(true);
              }}
            >
              {copied === 'text' ? 'Приглашение скопировано' : 'Копировать приглашение с инструкцией'}
            </Button>
            <Button variant="ghost" onClick={() => setShowText((v) => !v)} aria-expanded={showText}>
              {showText ? 'Скрыть текст' : 'Показать текст'}
            </Button>
          </div>
          {failed === 'text' ? (
            <p className="text-[13px] text-warning">
              Скопировать не получилось: выделите текст ниже и скопируйте его вручную.
            </p>
          ) : null}
          <p className="text-[12px] leading-5 text-ink-2">
            Текст целиком: ссылка, первые шаги, порядок работы на каждый день и разделы со ссылками — его
            можно вставить в WhatsApp, Telegram или письмо.
          </p>
          {showText ? (
            <Textarea
              readOnly
              rows={10}
              value={message.text}
              aria-label="Текст приглашения с инструкцией"
              onFocus={(e) => e.currentTarget.select()}
            />
          ) : null}
        </div>
      ) : null}

      {previewId ? <GuidePreview id={previewId} /> : null}
    </div>
  );
}

/** Что получит человек: та же инструкция, что в письме, — коротко, по заголовкам */
function GuidePreview({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['admin', 'invites', id, 'guide'],
    queryFn: () => api<StartGuide>(`/admin/invites/${id}/guide`),
    enabled: open,
  });
  return (
    <details
      className="rounded-md border border-line"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex min-h-11 cursor-pointer items-center px-4 text-[14px] font-medium text-ink md:min-h-10">
        Что получит человек
      </summary>
      <div className="border-t border-line px-4 py-3 text-[14px] leading-6">
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-ink-2">{q.data.intro}</p>
            <section>
              <h4 className="text-[13px] font-semibold text-ink">Первые шаги</h4>
              <ol className="mt-1 list-decimal pl-5 text-ink">
                {q.data.firstSteps.map((s) => (
                  <li key={s.key}>{s.title}</li>
                ))}
              </ol>
            </section>
            <section>
              <h4 className="text-[13px] font-semibold text-ink">Каждый день</h4>
              <ul className="mt-1 list-disc pl-5 text-ink">
                {q.data.daily.map((d) => (
                  <li key={d.key}>{d.title}</li>
                ))}
              </ul>
            </section>
            <section>
              <h4 className="text-[13px] font-semibold text-ink">Разделы</h4>
              <p className="mt-1 text-ink-2">{q.data.modules.map((m) => m.title).join(', ')}</p>
            </section>
          </div>
        )}
      </div>
    </details>
  );
}
